import React, { useState, useRef, useCallback, useMemo } from 'react';
import { MdCameraAlt, MdDelete, MdUpload, MdCompress } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { validateImageFile } from '@/services/cloudinary';
import ImageCropper from './ImageCropper';

// Types
interface ProfilePictureUploadProps {
  currentImageUrl?: string;
  onImageUpload: (imageUrl: string) => void;
  onImageRemove?: () => void;
  onImageCropped?: (file: File, previewUrl: string) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

// Constants
const SIZE_CONFIG = {
  sm: { container: 'w-16 h-16', icon: 'text-lg' },
  md: { container: 'w-20 h-20', icon: 'text-xl' },
  lg: { container: 'w-24 h-24', icon: 'text-2xl' },
} as const;



export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  onImageUpload,
  onImageRemove,
  onImageCropped,
  size = 'md',
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  // State
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressionInfo, setCompressionInfo] = useState<string | null>(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoized values
  const { container, icon } = useMemo(() => SIZE_CONFIG[size], [size]);
  const displayImage = useMemo(() => previewUrl || currentImageUrl, [previewUrl, currentImageUrl]);
  const isProcessing = useMemo(() => false, []);

  // Utility functions
  const clearStates = useCallback(() => {
    setError(null);
    setCompressionInfo(null);
    setPreviewUrl(null);
    setSelectedFile(null);
    setCropperOpen(false);
  }, []);

  // Event handlers
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file
      const validationError = validateImageFile(file);
      if (validationError) {
        setError(t(`portal.profile.professional.picture.${validationError}`));
        return;
      }
      setSelectedFile(file);
      setCropperOpen(true);
    }
  }, [t]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      // Validate file
      const validationError = validateImageFile(file);
      if (validationError) {
        setError(t(`portal.profile.professional.picture.${validationError}`));
        return;
      }
      setSelectedFile(file);
      setCropperOpen(true);
    }
  }, [t]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleRemove = useCallback(() => {
    clearStates();
    if (onImageRemove) {
      onImageRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [clearStates, onImageRemove]);

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // New: handle cropped image (no upload)
  const handleCroppedImage = useCallback((croppedFile: File) => {
    setCropperOpen(false);
    setSelectedFile(null);
    const preview = URL.createObjectURL(croppedFile);
    setPreviewUrl(preview);
    
    // Call parent callback with cropped file and preview
    if (onImageCropped) {
      onImageCropped(croppedFile, preview);
    }
  }, [onImageCropped]);

  // Render helpers
  const renderImage = () => (
    <img
      src={displayImage}
      alt={t('portal.profile.professional.picture.alt')}
      className="w-full h-full object-cover"
    />
  );

  const renderPlaceholder = () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <MdCameraAlt className={`text-gray-400 ${icon}`} />
    </div>
  );

  const renderUploadOverlay = () => (
    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
      <MdUpload className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
    </div>
  );

  const renderLoadingOverlay = () => (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mb-1" />
      </div>
    </div>
  );

  const renderActionButtons = () => (
    <div className="absolute -bottom-2 -right-2 flex space-x-1">
      <button
        type="button"
        onClick={handleClick}
        className="w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs transition-colors"
        title={t('portal.profile.professional.picture.change')}
      >
        <MdCameraAlt />
      </button>
      <button
        type="button"
        onClick={handleRemove}
        className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors"
        title={t('portal.profile.professional.picture.remove')}
      >
        <MdDelete />
      </button>
    </div>
  );

  const renderHelpText = () => (
    <div className="mt-2 text-xs text-gray-500 text-center">
      {t('portal.profile.professional.picture.uploadHelp')}
      <br />
      <span className="text-gray-400">
        {t('portal.profile.professional.picture.formats')}
      </span>
      <br />
      <span className="text-gray-400">
        {t('portal.profile.professional.picture.compressed')}
      </span>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      {/* Main upload area */}
      <div
        className={`
          ${container} relative rounded-full overflow-hidden border-2 border-dashed
          ${disabled ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-gray-400 hover:border-orange-500 cursor-pointer'}
          transition-colors duration-200
        `}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {displayImage ? renderImage() : renderPlaceholder()}
        {!disabled && !displayImage && renderUploadOverlay()}
        {isProcessing && renderLoadingOverlay()}
      </div>

      {/* Action buttons */}
      {!disabled && displayImage && renderActionButtons()}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || isProcessing}
      />

      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Compression info */}
      {compressionInfo && (
        <div className="mt-2 text-sm text-blue-600 flex items-center">
          <MdCompress className="mr-1" />
          {compressionInfo}
        </div>
      )}

      {/* Help text */}
      {!disabled && !displayImage && renderHelpText()}

      {/* ImageCropper Modal */}
      {cropperOpen && selectedFile && (
        <ImageCropper
          imageFile={selectedFile}
          onCrop={handleCroppedImage}
          onCancel={() => {
            setCropperOpen(false);
            setSelectedFile(null);
          }}
        />
      )}
    </div>
  );
};
